import React, { useEffect, useMemo, useState } from 'react';
import { clientsService, invoicesService } from '../../services/api';
import { fmtCurrency } from '../../utils/formatters';
import { TONE_BADGE } from '../../utils/statusColors';
import { LinhaDaCobranca } from '../../utils/filaDeCobranca';
import { campo, mensagemDoErroDeCarga } from '../../design';
import CascaDeModal from '../shared/CascaDeModal';
import { usePedirTexto } from '../shared/ConfirmDialog';
import { useAviso } from '../shared/Aviso';

/**
 * O envio MANUAL de cobrança, fora dos marcos da fila de sugestões. Veio inteiro da tela de Faturas na unificação
 * (decisão E1 do Heitor, 30/10/2026; prévia https://claude.ai/artifact/ScP3EnBjNsf4BFYeHtD9gm): texto sugerido e a
 * troca dele, observação, multa e juros, o ensaio para o próprio e-mail, a prévia de cada fatura, as que não saem agora
 * e o "Liberar cobrança" do cliente marcado "Não enviar".
 *
 * F1: com UMA fatura, os e-mails do Financeiro do cadastro são marcáveis e há um campo "outro e-mail". Sem mexer neles,
 * o envio vai para o cadastro, como antes, e o corpo NÃO leva `emails`: informar destino afrouxa a trava de e-mail
 * devolvido no servidor (`destinoExplicito`), e isso só pode acontecer quando o operador escolheu. O lote vai sempre para
 * o cadastro, porque o envio em lote não aceita destinatário por fatura.
 *
 * Só ADMIN chega aqui (G1): as rotas de envio são `@Roles(ADMIN)` no backend.
 */

/** Como cada texto aparece. O número segue no banco como tipo do template; a tela diz o que o texto faz. */
export const NIVEL_NOME: Record<1 | 2 | 3, string> = {
  1: 'Lembrete',
  2: 'Aviso',
  3: 'Boleto baixado',
};

/** O que a lista da cobrança traz além do que a fila usa: o estado do envio, que o modal precisa. */
export interface CandidatoDeCobranca extends LinhaDaCobranca {
  amountDue?: number;
  suggestedLevel?: 1 | 2 | 3;
  emails?: string[];
  foraDaJanela?: boolean;
  liberavel?: boolean;
  avisoFila?: string;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fantasma = 'px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap border border-central-borda bg-central-cartao text-central-texto hover:bg-central-elevado disabled:opacity-40';
const primaria = 'px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:bg-primary-strong disabled:opacity-50';

interface Progresso { jobId: string; total: number; completed: number; percent: number; errors: any[]; skipped?: any[]; status: string; falhou?: string }

export default function ModalDeCobranca({ candidatos, onClose, onMudou }: {
  candidatos: CandidatoDeCobranca[];
  onClose: () => void;
  /** A fila precisa recarregar: alguma cobrança saiu ou o cadastro do cliente mudou. */
  onMudou: () => void;
}) {
  const avisar = useAviso();
  const pedirTexto = usePedirTexto();
  const meuEmail = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || '{}')?.email || ''; } catch { return ''; }
  }, []);
  const [levelOverride, setLevelOverride] = useState<1 | 2 | 3 | null>(null);
  const [customNote, setCustomNote] = useState('');
  const [includeCharges, setIncludeCharges] = useState(true);
  const [teste, setTeste] = useState(false);
  const [emailDoTeste, setEmailDoTeste] = useState(meuEmail);
  const [enviando, setEnviando] = useState(false);
  const [recusa, setRecusa] = useState<string | null>(null);
  const [previa, setPrevia] = useState<{ code: string; subject: string; html: string; loading?: boolean } | null>(null);
  const [progresso, setProgresso] = useState<Progresso | null>(null);

  const umaSo = candidatos.length === 1 ? candidatos[0] : null;
  const doCadastro = useMemo(() => (umaSo?.emails || []).filter(Boolean), [umaSo]);
  const [marcados, setMarcados] = useState<Set<string>>(() => new Set(doCadastro));
  const [outro, setOutro] = useState('');
  const outros = outro.split(/[\s,;]+/).map((e) => e.trim()).filter(Boolean);
  const outroInvalido = outros.some((e) => !EMAIL.test(e));
  /** O operador mexeu no destino? Só então o corpo leva `emails`. */
  const destinoEscolhido = !!umaSo && (outros.length > 0 || doCadastro.some((e) => !marcados.has(e)));
  const destinos = [...doCadastro.filter((e) => marcados.has(e)), ...outros];

  // Com destino escolhido, a trava de e-mail devolvido não vale (é a válvula do servidor). As outras valem.
  const podeSair = (c: CandidatoDeCobranca) => !c.blockedReason || (destinoEscolhido && c.blockedKind === 'entrega');
  const enviaveis = candidatos.filter(podeSair);
  const bloqueadas = candidatos.filter((c) => !podeSair(c));
  const totalEnviavel = enviaveis.reduce((s, c) => s + Number(c.amountDue ?? c.originalValue ?? 0), 0);
  const nivelDe = (c: CandidatoDeCobranca) => levelOverride || c.suggestedLevel || 1;
  // J1 (02/11/2026): órgão público nunca leva multa e juros, e quem decide é o servidor. A tela só não oferece a opção
  // onde ela não vale: some quando todas são de órgão, e no lote misto ela vale só para as privadas.
  const temOrgao = candidatos.some((c) => c.isPublicSector);
  const temPrivada = candidatos.some((c) => !c.isPublicSector);
  const encargosDe = (c: CandidatoDeCobranca) => includeCharges && !c.isPublicSector;
  const destinoDoTeste = emailDoTeste.trim();
  const quantas = teste ? candidatos.length : enviaveis.length;
  const travado = enviando
    || (teste ? !EMAIL.test(destinoDoTeste) : enviaveis.length === 0 || (destinoEscolhido && (destinos.length === 0 || outroInvalido)));

  const verEmail = async (c: CandidatoDeCobranca) => {
    setPrevia({ code: c.code, subject: '', html: '', loading: true });
    try {
      const r = await invoicesService.dunningPreview(c.id, nivelDe(c), encargosDe(c));
      setPrevia({ code: c.code, subject: r.subject, html: r.html });
    } catch (e) {
      setPrevia(null);
      avisar(`Não consegui montar a prévia: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
  };

  const liberar = async (c: CandidatoDeCobranca) => {
    const motivo = await pedirTexto({
      title: `Liberar cobrança por e-mail para ${c.clientName}?`,
      consequence: 'O cadastro está como "Não enviar", o que vale para NF, fatura e boleto. Liberar aqui vale só para a cobrança: os documentos continuam não sendo enviados.',
      label: 'Por quê? (fica registrado no cadastro)',
      valorInicial: 'Recebe documentos pelo portal próprio, mas aceita cobrança por e-mail.',
      obrigatorio: true,
      validar: (t) => (t.length < 3 ? 'Informe o motivo: ele fica no cadastro para quem for revisar depois.' : null),
      confirmLabel: 'Liberar cobrança',
    });
    if (motivo === null) return;
    try {
      await clientsService.setDunningMode(c.clientId, 'manual', motivo.trim());
      onMudou();
    } catch (e) {
      avisar(`Não foi possível liberar: ${mensagemDoErroDeCarga(e)}`, 'error');
    }
  };

  const enviar = async () => {
    if (travado) return;
    setEnviando(true);
    setRecusa(null);
    const comum = { level: levelOverride || undefined, customNote: customNote.trim() || undefined, includeCharges: temPrivada && includeCharges };
    try {
      if (!teste && umaSo) {
        // Uma fatura: o envio direto, que devolve na hora se saiu ou por que não saiu.
        const r = await invoicesService.sendDunning(umaSo.id, { ...comum, ...(destinoEscolhido ? { emails: destinos } : {}) });
        if (r?.sent) {
          const n = (r.sentTo || destinos).length;
          avisar(`Cobrança de ${umaSo.code} enviada${n ? ` para ${n} ${n === 1 ? 'e-mail' : 'e-mails'}` : ''}.`, 'success');
          onMudou();
          onClose();
          return;
        }
        setRecusa(r?.error || 'O servidor não enviou e não disse o motivo.');
        setEnviando(false);
        return;
      }
      const job = await invoicesService.sendDunningBatch({
        invoiceIds: teste ? candidatos.map((c) => c.id) : enviaveis.map((c) => c.id),
        ...comum,
        ...(teste ? { test: true, testEmails: [destinoDoTeste] } : {}),
      });
      if (job?.jobId) {
        setProgresso({ jobId: job.jobId, total: quantas, completed: 0, percent: 0, errors: [], status: 'processing' });
      } else {
        onMudou();
        onClose();
      }
    } catch (e) {
      setRecusa(`Não enviou: ${mensagemDoErroDeCarga(e)}`);
      setEnviando(false);
    }
  };

  // O lote roda no servidor: a barra acompanha o job. Falha seguida do acompanhamento para e diz que não sabe.
  useEffect(() => {
    if (!progresso || progresso.status === 'done' || progresso.falhou) return;
    let falhas = 0;
    const t = setInterval(async () => {
      try {
        const p = await invoicesService.getBatchProgress(progresso.jobId);
        falhas = 0;
        setProgresso((prev) => (prev ? { ...prev, ...p } : prev));
        if (p?.status === 'done') { clearInterval(t); onMudou(); }
      } catch (e) {
        if (++falhas < 5) return;
        clearInterval(t);
        setProgresso((prev) => (prev ? { ...prev, falhou: mensagemDoErroDeCarga(e) } : prev));
        onMudou();
      }
    }, 1500);
    return () => clearInterval(t);
  }, [progresso?.jobId, progresso?.status, progresso?.falhou]); // eslint-disable-line react-hooks/exhaustive-deps

  if (progresso) {
    const acabou = progresso.status === 'done' || !!progresso.falhou;
    return (
      <CascaDeModal
        largura='xs'
        fechavel={acabou}
        titulo={acabou ? (teste ? 'Teste enviado' : 'Cobrança enviada') : 'Enviando cobranças…'}
        onClose={onClose}
        corpoClassName='p-6'
        rodape={acabou ? <button type='button' className={`ml-auto ${fantasma}`} onClick={onClose}>Fechar</button> : undefined}
      >
        <div className='bg-central-elevado rounded-full h-3 overflow-hidden'>
          <div className='bg-primary h-3 rounded-full transition-all duration-300' style={{ width: `${progresso.percent}%` }} />
        </div>
        <p className='text-sm text-central-texto mt-2' data-progresso>{progresso.completed}/{progresso.total} ({progresso.percent}%)</p>
        {progresso.falhou && (
          <p role='alert' className={`mt-3 px-3 py-2 rounded-lg text-sm ${TONE_BADGE.warning}`}>
            Perdi o acompanhamento ({progresso.falhou}). O envio roda no servidor e pode ter terminado: confira a fila antes de cobrar de novo.
          </p>
        )}
        {(progresso.errors.length > 0 || (progresso.skipped?.length || 0) > 0) && (
          <div className='mt-3 space-y-1 text-xs' data-falhas-do-lote>
            {progresso.errors.map((e: any, i: number) => <p key={`e${i}`} className='text-red-400'>Fatura {e.code || e.id}: {e.error}</p>)}
            {(progresso.skipped || []).map((s: any, i: number) => <p key={`s${i}`} className='text-amber-400'>{s.code || `#${s.id}`}: {s.reason}</p>)}
          </div>
        )}
      </CascaDeModal>
    );
  }

  return (
    <>
      <CascaDeModal
        largura='lg'
        titulo={umaSo ? `Cobrar ${umaSo.code}` : 'Cobrar faturas vencidas'}
        subtitulo={umaSo ? umaSo.clientName : `${candidatos.length} faturas selecionadas`}
        onClose={onClose}
        temEdicaoNaoSalva={() => !!customNote.trim() || !!outro.trim() || levelOverride !== null || teste}
        corpoClassName='px-5 py-4'
        rodape={
          <>
            <button type='button' className={`flex-1 ${fantasma}`} onClick={onClose}>Cancelar</button>
            <button type='button' className={`flex-1 ${primaria}`} disabled={travado} onClick={enviar}>
              {enviando
                ? 'Enviando…'
                : teste
                  ? `Enviar teste de ${quantas} ${quantas === 1 ? 'e-mail' : 'e-mails'} para ${destinoDoTeste || '—'}`
                  : `Cobrar ${quantas} ${quantas === 1 ? 'fatura' : 'faturas'} · ${fmtCurrency(totalEnviavel)}`}
            </button>
          </>
        }
      >
        <p className='text-sm text-central-texto-fraco mb-4'>
          O texto é escolhido pela situação do boleto de cada fatura. Nenhum deles fala em suspensão de serviço ou cobrança judicial.
        </p>

        {recusa && (
          <p role='alert' className={`mb-4 px-3 py-2 rounded-lg text-sm ${TONE_BADGE.danger}`} data-recusa-do-envio>{recusa}</p>
        )}

        {enviaveis.length > 0 && (
          <div className='overflow-x-auto mb-4 border border-central-borda rounded-lg'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='text-central-texto-fraco text-xs uppercase'>
                  <th className='text-left px-3 py-2 font-medium'>Fatura</th>
                  <th className='text-left px-3 py-2 font-medium'>Cliente</th>
                  <th className='text-right px-3 py-2 font-medium'>Atraso</th>
                  <th className='text-right px-3 py-2 font-medium'>A cobrar</th>
                  <th className='text-left px-3 py-2 font-medium'>Texto</th>
                  <th className='px-3 py-2' />
                </tr>
              </thead>
              <tbody>
                {enviaveis.map((c) => {
                  const nivel = nivelDe(c);
                  return (
                    <tr key={c.id} className='border-t border-central-borda' data-enviavel={c.code}>
                      <td className='px-3 py-2 text-central-texto font-mono text-xs'>{c.code}</td>
                      <td className='px-3 py-2 text-central-texto'>
                        {c.clientName}
                        {(c.dunningCount || 0) > 0 && <span className='ml-2 text-[11px] text-amber-400'>já cobrada {c.dunningCount}×</span>}
                      </td>
                      <td className='px-3 py-2 text-right text-central-texto-fraco tabular-nums'>{c.daysOverdue}d</td>
                      <td className='px-3 py-2 text-right tabular-nums'>
                        <span className='text-central-texto font-semibold'>{fmtCurrency(c.amountDue ?? c.originalValue)}</span>
                        {c.isPublicSector
                          ? <span className='block text-[11px] text-central-texto-fraco' data-sem-encargos>órgão público, sem multa e juros</span>
                          : c.paymentMode === 'pix' && <span className='block text-[11px] text-amber-400'>PIX, com encargos</span>}
                      </td>
                      <td className='px-3 py-2'>
                        <span className='text-central-texto'>{NIVEL_NOME[nivel]}</span>
                        {levelOverride && c.suggestedLevel && nivel !== c.suggestedLevel && (
                          <span className='block text-[11px] text-amber-400'>forçado (sugerido: {NIVEL_NOME[c.suggestedLevel]})</span>
                        )}
                      </td>
                      <td className='px-3 py-2 text-right'>
                        <button type='button' className='text-xs text-central-texto-fraco hover:text-central-texto underline' onClick={() => verEmail(c)}>Ver e-mail</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {bloqueadas.length > 0 && (
          <div className='mb-4 p-3 rounded-lg bg-central-elevado/60 border border-central-borda' data-bloqueadas>
            <p className='text-central-texto text-sm font-medium mb-1'>
              {bloqueadas.length === 1 ? 'Esta fatura não sai agora:' : `${bloqueadas.length} faturas não saem agora:`}
            </p>
            {bloqueadas.map((c) => (
              <div key={c.id} className='text-central-texto-fraco text-xs py-0.5'>
                <span className='font-mono'>{c.code}</span> {c.clientName}: {c.blockedReason}
                {c.blockedKind === 'entrega' && umaSo && <span> Escolha outro destino abaixo para mandar mesmo assim.</span>}
                {c.liberavel && (
                  <button type='button' className={`ml-2 ${fantasma} !py-0.5 !text-xs`} onClick={() => liberar(c)}
                    title='Mantém os documentos indo pelo portal e libera só a cobrança. Pede o motivo e grava no cadastro.'>
                    Liberar cobrança para este cliente
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {enviaveis.some((c) => c.avisoFila) && (
          <div className='mb-4 p-3 rounded-lg bg-central-elevado/60 border border-central-borda'>
            <p className='text-central-texto text-sm font-medium mb-1'>A régua automática não mandaria {enviaveis.filter((c) => c.avisoFila).length === 1 ? 'esta' : 'estas'}, mas você pode:</p>
            {enviaveis.filter((c) => c.avisoFila).map((c) => (
              <p key={c.id} className='text-central-texto-fraco text-xs'><span className='font-mono'>{c.code}</span> {c.clientName}: {c.avisoFila}</p>
            ))}
          </div>
        )}

        {enviaveis.some((c) => c.foraDaJanela) && (
          <p className={`mb-4 px-3 py-2 rounded-lg text-sm ${TONE_BADGE.warning}`}>
            Fora do horário comercial. O cliente vai receber agora. Se preferir, dispare em dia útil entre 8h e 18h.
          </p>
        )}

        {umaSo && !teste && (
          <fieldset className='mb-4' data-destinatarios>
            <legend className='text-xs text-central-texto-fraco uppercase font-semibold mb-2'>Para</legend>
            {doCadastro.length === 0 && <p className='text-sm text-amber-300 mb-1'>O cliente não tem e-mail do Financeiro no cadastro. Informe um abaixo.</p>}
            {doCadastro.map((e) => (
              <label key={e} className='flex items-center gap-2 text-sm text-central-texto py-0.5'>
                <input type='checkbox' checked={marcados.has(e)} onChange={() => setMarcados((s) => { const n = new Set(s); if (n.has(e)) n.delete(e); else n.add(e); return n; })} />
                {e} <span className='text-xs text-central-texto-fraco'>(cadastro)</span>
              </label>
            ))}
            <label className='block text-xs text-central-texto-fraco mt-2'>Outro e-mail
              <input type='text' value={outro} onChange={(e) => setOutro(e.target.value)} placeholder='separe por vírgula se for mais de um'
                className={`mt-1 block w-full px-3 py-1.5 rounded-lg text-sm ${campo}`} />
            </label>
            {outroInvalido && <p className='text-xs text-red-400 mt-1'>Confira o e-mail digitado.</p>}
            <p className='text-xs text-central-texto-fraco mt-1'>Sem mudar nada, vai para os e-mails do cadastro. Trocar aqui vale só para este envio e não mexe no cadastro.</p>
          </fieldset>
        )}

        <div className='mb-4'>
          <label className='block text-xs text-central-texto-fraco uppercase font-semibold mb-2' htmlFor='cobranca-texto'>Texto do e-mail</label>
          <select id='cobranca-texto' value={levelOverride ?? ''} onChange={(e) => setLevelOverride(e.target.value ? (Number(e.target.value) as 1 | 2 | 3) : null)}
            className={`w-full px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`}>
            <option value=''>Automático: lembrete com boleto válido, regularização quando o banco baixou</option>
            {([1, 2, 3] as const).map((n) => <option key={n} value={n}>Forçar "{NIVEL_NOME[n]}" {umaSo ? '' : 'em todas'}</option>)}
          </select>
        </div>

        <div className='mb-4'>
          <label className='block text-xs text-central-texto-fraco uppercase font-semibold mb-2' htmlFor='cobranca-nota'>Observação no e-mail (opcional)</label>
          <textarea id='cobranca-nota' value={customNote} onChange={(e) => setCustomNote(e.target.value)} placeholder='Ex: Conforme conversamos por telefone hoje.' rows={2}
            className={`w-full px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none resize-none`} />
        </div>

        {temOrgao && (
          <p className='mb-3 text-sm text-central-texto' data-orgao-sem-encargos>
            {temPrivada ? 'Órgão público vai sem multa e juros. A opção abaixo vale só para as privadas.' : 'Órgão público vai sem multa e juros.'}
          </p>
        )}
        {temPrivada && (
          <label className='flex items-start gap-2 mb-5 cursor-pointer'>
            <input type='checkbox' checked={includeCharges} onChange={(e) => setIncludeCharges(e.target.checked)} className='mt-1' />
            <span className='text-sm text-central-texto'>
              Cobrar multa e juros{temOrgao ? ' das privadas' : ''}
              <span className='block text-xs text-central-texto-fraco'>Vale só para fatura sem boleto válido, onde o valor vai fechado no PIX. Com boleto ativo quem calcula é o banco, na data do pagamento.</span>
            </span>
          </label>
        )}

        {/* Ensaio: testar não grava histórico, não consome a cadência de 7 dias nem o teto de 5. */}
        <label className='flex items-start gap-2 mb-3 cursor-pointer'>
          <input type='checkbox' checked={teste} onChange={(e) => setTeste(e.target.checked)} className='mt-1' />
          <span className='text-sm text-central-texto'>
            Enviar como teste
            <span className='block text-xs text-central-texto-fraco'>O e-mail sai idêntico ao que o cliente receberia, mas vai só para o endereço abaixo. Não conta como cobrança: não grava histórico, não consome a cadência nem o teto.</span>
          </span>
        </label>
        {teste && (
          <div className='pl-6'>
            <input type='email' aria-label='E-mail do teste' value={emailDoTeste} onChange={(e) => setEmailDoTeste(e.target.value)} placeholder='para onde enviar o teste'
              className={`w-full px-3 py-2 ${campo} rounded-lg text-sm focus:outline-none`} />
            <p className='text-xs mt-1.5 text-central-texto-fraco'>O cliente não recebe nada. O e-mail dele nem é consultado neste modo.</p>
          </div>
        )}
      </CascaDeModal>

      {previa && (
        <CascaDeModal
          largura='md'
          zIndex='z-[60]'
          fecharAoClicarFora
          titulo={previa.subject || 'Gerando…'}
          subtitulo={`Prévia · ${previa.code}`}
          onClose={() => setPrevia(null)}
          corpoClassName='bg-slate-200'
        >
          {previa.loading
            ? <p className='p-8 text-center text-slate-600'>Gerando prévia…</p>
            : <iframe title={`Prévia da cobrança ${previa.code}`} srcDoc={previa.html} sandbox='' className='w-full h-[70vh] border-0' />}
        </CascaDeModal>
      )}
    </>
  );
}
